import * as React from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Image as ImageIcon,
  Code,
  Pilcrow,
  Strikethrough,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { MediaPicker } from '@/components/media/picker';
import { useMediaHost, type MediaAsset } from '@/lib/media';

/**
 * <RichTextEditor> — TipTap-backed WYSIWYG editor that emits sanitized
 * HTML via `onChange`. Used in /tresci/aktualnosci/* and /tresci/realizacje/*
 * for the long-form `body_html` field.
 *
 * Image inserts route through the existing media library — opens the
 * shared <MediaPicker> dialog, then inserts an <img src> pointing at the
 * tenant media host. No uploads happen here; uploads live in /media.
 */
export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number | string;
  disabled?: boolean;
  className?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 320,
  disabled,
  className,
}: RichTextEditorProps) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const mediaHost = useMediaHost();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'h2m-rt-image',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Zacznij pisać…',
      }),
    ],
    content: value || '<p></p>',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm sm:prose-base max-w-none focus:outline-none',
          'min-h-[var(--rt-min-h)] px-4 py-3',
        ),
        spellcheck: 'true',
      },
    },
    immediatelyRender: false,
  });

  // Keep external value in sync (e.g., when switching between entries).
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
    } else if (!value && current !== '<p></p>') {
      editor.commands.setContent('<p></p>', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div
        className="rounded-md bg-surface ring-1 ring-border"
        style={{ minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }}
      />
    );
  }

  const insertImage = (asset: MediaAsset) => {
    const url = `${mediaHost}${asset.public_url}`;
    editor
      .chain()
      .focus()
      .setImage({ src: url, alt: asset.alt_text ?? asset.original_filename })
      .run();
    setPickerOpen(false);
  };

  const onAddLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL linku', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md bg-bg ring-1 ring-border focus-within:ring-accent/40',
        disabled && 'opacity-60',
        className,
      )}
      style={{ ['--rt-min-h' as never]: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }}
    >
      <RichTextToolbar editor={editor} onAddLink={onAddLink} onPickImage={() => setPickerOpen(true)} />
      <EditorContent editor={editor} />
      {pickerOpen ? (
        <MediaPicker
          title="Wybierz obraz do wstawienia"
          onSelect={insertImage}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

function ToolBtn({
  active,
  disabled,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface hover:text-text disabled:opacity-40',
        active && 'bg-accent/10 text-accent ring-1 ring-accent/20',
      )}
    >
      {children}
    </button>
  );
}

function ToolDivider() {
  return <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />;
}

function RichTextToolbar({
  editor,
  onAddLink,
  onPickImage,
}: {
  editor: Editor;
  onAddLink: () => void;
  onPickImage: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-surface px-2 py-1.5">
      <ToolBtn
        label="Cofnij"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Ponów"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolDivider />
      <ToolBtn
        label="Paragraf"
        active={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}
      >
        <Pilcrow className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Nagłówek H1"
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Nagłówek H2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Nagłówek H3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolDivider />
      <ToolBtn
        label="Pogrubienie"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Kursywa"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Przekreślenie"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Kod inline"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolDivider />
      <ToolBtn
        label="Lista punktowana"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Lista numerowana"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn
        label="Cytat"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolDivider />
      <ToolBtn label="Wstaw link" active={editor.isActive('link')} onClick={onAddLink}>
        <Link2 className="size-4" aria-hidden="true" />
      </ToolBtn>
      <ToolBtn label="Wstaw obraz z biblioteki" onClick={onPickImage}>
        <ImageIcon className="size-4" aria-hidden="true" />
      </ToolBtn>
      <div className="ml-auto pr-1 text-[11px] text-text-subtle">
        {editor.storage.characterCount?.characters?.() ?? ''}
      </div>
    </div>
  );
}
